import { useCallback, useRef } from 'react'
import { useRecoilCallback } from 'recoil'
import { useTranslation } from 'react-i18next'
import type { Item, TextItem } from '@messenger/types'
import {
  itemAtomFamily,
  itemIdAtom,
  unsavedChangesAtom,
} from '@/atoms/_editorAtoms'
import { useEditorHtmlConversionContext } from '@/hooks/editor/useEditorHtmlConversionContext'
import {
  useEditorInsertTextBox,
  useEditorTextBoxSeedOrigin,
} from '@/hooks/editor/useEditorInsertTextBox'
import { useIsInternalAdmin } from '@/hooks/utility/useIsInternalAdmin'
import { useSetToast } from '@/hooks/utility/useSetToast'
import {
  editorTextBoxConversionToastText,
  editorTextBoxFromHtmlInsert,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import { logger, EventCategory } from '@/utils/logger'

/**
 * What an HTML insert made: a Lotus box, the TinyMCE item, or nothing, when
 * the box's write failed or an insert from the same control is still running.
 */
export type EditorHtmlInsertOutcome = 'lotus' | 'tinymce' | null

/**
 * Inserts the TinyMCE text item a custom verse or a service template makes
 * as a Lotus box instead: its HTML converted, at the toolbar seed's origin,
 * the item's width and the seed height, under the item's id. HTML the
 * converter refuses inserts the TinyMCE item as it is, with no toast. A
 * conversion that did not carry everything is logged, and only internal
 * staff see a toast naming what was lost. A second insert started while one
 * is still writing does nothing, so a double click makes one box.
 */
export const useEditorInsertHtmlTextBox = () => {
  const { t, i18n } = useTranslation()
  const isInternalAdmin = useIsInternalAdmin()
  const setToast = useSetToast()
  const readSeedOrigin = useEditorTextBoxSeedOrigin()
  const insertEditorTextBox = useEditorInsertTextBox()

  const readInsertContext = useEditorHtmlConversionContext()

  const addTinyMceItem = useRecoilCallback(
    ({ snapshot, set }) =>
      (item: TextItem) => {
        const itemIds = snapshot.getLoadable(itemIdAtom).valueMaybe() ?? []
        set(itemAtomFamily(item.id), item as Item)
        set(itemIdAtom, [...itemIds, item.id])
        set(unsavedChangesAtom, true)
      },
    [],
  )

  const inFlight = useRef(false)

  const insertHtmlTextBox = useCallback(
    async (item: TextItem): Promise<EditorHtmlInsertOutcome> => {
      const { pageId, caseServiceIds } = readInsertContext()
      const result = editorTextBoxFromHtmlInsert({
        boxId: item.id,
        html: item.text.value,
        origin: await readSeedOrigin(),
        width: item.width,
        zIndex: item.z,
        caseServiceIds,
      })
      if (!result.ok) {
        logger.info({
          message: 'editor_insert_html_text_box_refused',
          category: EventCategory.INSERT,
          data: { pageId, itemId: item.id, reason: result.refusal.reason },
        })
        addTinyMceItem(item)
        return 'tinymce'
      }
      const { box, report } = result.conversion
      const boxId = await insertEditorTextBox(box)
      if (boxId === null) return null
      if (report.length > 0) {
        logger.info({
          message: 'editor_insert_html_text_box_report',
          category: EventCategory.INSERT,
          data: { pageId, boxId, report },
        })
        if (isInternalAdmin)
          setToast({
            text: editorTextBoxConversionToastText(
              report,
              t,
              i18n.resolvedLanguage,
            ),
          })
      }
      return 'lotus'
    },
    [
      readInsertContext,
      readSeedOrigin,
      addTinyMceItem,
      insertEditorTextBox,
      isInternalAdmin,
      setToast,
      t,
      i18n,
    ],
  )

  return useCallback(
    async (item: TextItem): Promise<EditorHtmlInsertOutcome> => {
      if (inFlight.current) return null
      inFlight.current = true
      try {
        return await insertHtmlTextBox(item)
      } finally {
        inFlight.current = false
      }
    },
    [insertHtmlTextBox],
  )
}
